import React, { useEffect } from "react";
import { Platform } from "react-native";

import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedProps,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { Circle, G } from "react-native-svg";

import { BOARD_BG } from "../theme/boardColors";
import { ringWidth } from "./board/constants";
import { strokeWidthProps } from "./board/strokes";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

const OUTLINE_WIDTH = 1.5;

/**
 * The current node's breathing pulse, as develop had it (-1 / +1.5 viewBox
 * units at r = 10): trough -10% and peak +15% of the radius, so -1.8px and
 * +2.7px on the 18px current node.
 */
export const BREATH_LOW = -0.1;
export const BREATH_HIGH = 0.15;
const ease = Easing.inOut(Easing.ease);

export interface TouchableCircleProps {
  /** Radius in board units (px). Centred on 0,0; pops scale a parent G. */
  r: number;
  /** Body colour; for a hollow node, the ring colour. */
  fill: string;
  /** Draw an empty ring (the target) instead of a solid disc. */
  hollow?: boolean;
  /** How far a hollow ring has filled with its colour (the won target). */
  fillLevel?: number;
  /** Breathe (the current node of a game in progress, motion allowed). */
  breathing?: boolean;
  /** Fill transition; 0 under reduced motion. */
  fillDuration?: number;
  /** Board units per screen pixel, for the non-scaling-stroke fallback. */
  unitsPerPx?: number;
  onPress: () => void;
  testID?: string;
}

/**
 * One board node: a solid disc with a dark outline, a fill tween and (when
 * current) the breathing pulse, or (the target) an empty ring cased in the
 * same dark outline on both sides.
 */
const TouchableCircle: React.FC<TouchableCircleProps> = ({
  r,
  fill,
  hollow = false,
  fillLevel = 0,
  breathing = false,
  fillDuration = 120,
  unitsPerPx = 1,
  onPress,
  testID,
}) => {
  const fillColor = useSharedValue(fill);
  const radius = useSharedValue(r);
  const breathed = React.useRef(false);

  useEffect(() => {
    if (breathing) {
      // Ease to the trough, then inhale 2.5s / exhale 3s, forever.
      breathed.current = true;
      const low = r * (1 + BREATH_LOW);
      radius.value = withSequence(
        withTiming(low, { duration: 500, easing: Easing.out(Easing.ease) }),
        withRepeat(
          withSequence(
            withTiming(r * (1 + BREATH_HIGH), { duration: 2500, easing: ease }),
            withTiming(low, { duration: 3000, easing: ease }),
          ),
          -1,
          false,
        ),
      );
    } else if (breathed.current) {
      // No longer current: ease back to the resting radius.
      breathed.current = false;
      cancelAnimation(radius);
      radius.value = withTiming(r, { duration: 500, easing: ease });
    } else {
      radius.value = r;
    }
  }, [breathing, r, radius]);

  useEffect(() => () => cancelAnimation(radius), [radius]);

  useEffect(() => {
    fillColor.value =
      fillDuration > 0
        ? withTiming(fill, {
            duration: fillDuration,
            easing: Easing.inOut(Easing.ease),
          })
        : fill;
  }, [fill, fillDuration, fillColor]);

  const bodyProps = useAnimatedProps(
    () =>
      ({ r: radius.value, fill: fillColor.value }) as {
        r: number;
        fill: string;
      },
  );

  const pressHandler =
    Platform.OS === "web" ? { onClick: onPress } : { onPress };

  if (hollow) {
    // Coloured ring just inside r; the casing shows 1.5px either side of it.
    // The inside is filled with the board colour: it reads as empty and hides
    // the end of the edge that runs into the node's centre. On a win the
    // inside fills with the ring colour (fillLevel 0 -> 1).
    const ring = ringWidth(r);
    const rr = Math.max(r - ring / 2 - OUTLINE_WIDTH / 2, 1);
    const level = Math.max(0, Math.min(1, fillLevel));
    return (
      <G testID={testID}>
        <Circle
          cx={0}
          cy={0}
          r={rr}
          fill={BOARD_BG}
          stroke={BOARD_BG}
          {...strokeWidthProps(ring + 2 * OUTLINE_WIDTH, unitsPerPx)}
          {...pressHandler}
        />
        {level > 0 && (
          <Circle
            cx={0}
            cy={0}
            r={rr}
            fill={fill}
            opacity={level}
            testID={testID ? `${testID}-fill` : undefined}
            {...pressHandler}
          />
        )}
        <Circle
          cx={0}
          cy={0}
          r={rr}
          fill="none"
          stroke={fill}
          {...strokeWidthProps(ring, unitsPerPx)}
          {...pressHandler}
        />
      </G>
    );
  }

  return (
    <AnimatedCircle
      testID={testID}
      cx={0}
      cy={0}
      r={r}
      fill={fill}
      stroke={BOARD_BG}
      {...strokeWidthProps(OUTLINE_WIDTH, unitsPerPx)}
      {...pressHandler}
      animatedProps={bodyProps}
    />
  );
};

export default TouchableCircle;
